/** Hard limits, tunable constants and language/engine tables. */

export const MAX_IMAGE_BYTES = 10 * 1024 * 1024;
export const MAX_PDF_BYTES = 20 * 1024 * 1024;
export const MAX_MEGAPIXELS = 25;
export const MAX_DECODED_PIXELS = MAX_MEGAPIXELS * 1_000_000;
export const MAX_BATCH_IMAGES = 20;
export const MAX_PDF_PAGES = 20;

/** Safety cap for a single preprocessing variant canvas. */
export const MAX_VARIANT_PIXELS = 12_000_000;

/**
 * Median-luminance cut-off used to decide that an OCR canvas holds light text
 * on a dark background. Tune this after testing with real photographs.
 */
export const DARK_BACKGROUND_LUMINANCE_THRESHOLD = 100;

/** Grid resolution used when sampling luminance across the OCR canvas. */
export const LUMINANCE_SAMPLE_GRID = 50;

/** Average confidence under which we surface "Review recommended". */
export const LOW_CONFIDENCE_THRESHOLD = 70;

/** Average confidence under which Classic OCR tries one alternative variant. */
export const FALLBACK_CONFIDENCE_THRESHOLD = 62;

/**
 * Median glyph height, measured in ORIGINAL image pixels, below which text is
 * genuinely too small to recognise dependably. Upscaling cannot invent pixels.
 */
export const MIN_RELIABLE_TEXT_HEIGHT_PX = 11;

export const LOW_RESOLUTION_NOTICE =
  'Some text is too small in the original image. For better accuracy, upload the original screen or a higher-resolution image.';

export const SUPPORTED_EXTENSIONS = ['jpg', 'jpeg', 'png', 'webp', 'bmp', 'pdf'] as const;

export const SUPPORTED_MIME_TYPES = [
  'image/jpeg',
  'image/jpg',
  'image/png',
  'image/webp',
  'image/bmp',
  'image/x-ms-bmp',
  'application/pdf',
] as const;

export type LanguageId = 'english' | 'hindi' | 'sanskrit' | 'devanagari' | 'mixed';

export interface LanguageOption {
  id: LanguageId;
  label: string;
  shortLabel: string;
  /** Language array handed straight to the installed Tesseract.js API. */
  tesseract: string[];
  script: 'latin' | 'devanagari' | 'mixed';
}

export const LANGUAGE_OPTIONS: LanguageOption[] = [
  { id: 'english', label: 'English', shortLabel: 'English', tesseract: ['eng'], script: 'latin' },
  { id: 'hindi', label: 'Hindi (Devanagari)', shortLabel: 'Hindi', tesseract: ['hin'], script: 'devanagari' },
  { id: 'sanskrit', label: 'Sanskrit (Devanagari)', shortLabel: 'Sanskrit', tesseract: ['san'], script: 'devanagari' },
  {
    id: 'devanagari',
    label: 'Hindi + Sanskrit (Devanagari)',
    shortLabel: 'Hindi + Sanskrit',
    tesseract: ['hin', 'san'],
    script: 'devanagari',
  },
  {
    id: 'mixed',
    label: 'Mixed: English + Hindi + Sanskrit',
    shortLabel: 'Mixed',
    tesseract: ['eng', 'hin', 'san'],
    script: 'mixed',
  },
];

export const DEFAULT_LANGUAGE_ID: LanguageId = 'english';
export const LANGUAGE_SESSION_KEY = 'textExtractor.languageId';

export function getLanguageOption(id: LanguageId): LanguageOption {
  return LANGUAGE_OPTIONS.find((l) => l.id === id) || LANGUAGE_OPTIONS[0];
}

/** Tesseract language array for a language selection. */
export function tesseractLangsFor(id: LanguageId): string[] {
  return getLanguageOption(id).tesseract;
}

export function isDevanagariOnly(id: LanguageId): boolean {
  return getLanguageOption(id).script === 'devanagari';
}

export type EngineId = 'classic' | 'nextgen';

export type RecognitionModeId = 'document' | 'poster';

export interface RecognitionModeOption {
  id: RecognitionModeId;
  label: string;
  hint: string;
}

export const RECOGNITION_MODES: RecognitionModeOption[] = [
  {
    id: 'document',
    label: 'Document / Screenshot',
    hint: 'Pages, screenshots, receipts, invoices and tables where the text runs in normal lines.',
  },
  {
    id: 'poster',
    label: 'Poster / Mixed Layout',
    hint: 'WhatsApp cards, flyers and posters with scattered text, many colours or dark backgrounds.',
  },
];

export const NEXTGEN_NON_ENGLISH_NOTICE =
  'Next-Gen OCR is currently available only for English in this app.';

/**
 * Self-hosted, version-pinned Tesseract trained data.
 * - /tessdata        → @tesseract.js-data/{eng,hin,san}@1.0.0 · 4.0.0_best_int (compact, English default)
 * - /tessdata-best   → tessdata_best@4.1.0 hin + san (full accuracy models) with the compact eng model
 */
export const TESSDATA_DIR = 'tessdata';
export const TESSDATA_BEST_DIR = 'tessdata-best';

export const TESSDATA_SOURCE =
  'English: @tesseract.js-data/eng@1.0.0 (4.0.0_best_int). Hindi & Sanskrit: tesseract-ocr/tessdata_best@4.1.0 — both self-hosted by this app and cached by your browser after the first download.';

/** Devanagari configurations use the full-accuracy tessdata_best models. */
export function tessdataDirFor(langs: string[]): string {
  return langs.some((l) => l === 'hin' || l === 'san') ? TESSDATA_BEST_DIR : TESSDATA_DIR;
}

export const PDF_WORKER_FILE = 'pdf.worker.min.mjs';

export const PRIVACY_STATEMENT =
  'Your files are processed in your browser. Images and extracted text are not uploaded or stored.';
